import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, SimpleGrid, VStack } from '@chakra-ui/react';
import { printFormat } from 'iban';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { TeamMemberRequestStatusBadge } from 'components/TeamMemberRequestStatusBadge';

import { BankTransferRequestsListItemRequestFragment } from '../../../../../../hooks/useManagedUserBankTransferRequests/managedUserBankTransferRequests.gql';
import * as routes from '../../../../../../routes';
import locales from './locales';

interface BankTransferRequestListItemProps {
  isSelected: boolean;
  request: BankTransferRequestsListItemRequestFragment;
}

const BankTransferRequestListItem: FC<BankTransferRequestListItemProps> = ({
  isSelected,
  request,
}) => {
  const recipientName = request.requesterPayload.bankTransferRecipient.name;
  const iban = printFormat(request.requesterPayload.bankTransferRecipient.iban);
  const formattedIban = locales.formatString(locales.maskedIban, {
    panEnd: iban.slice(-3),
    panStart: iban.slice(0, 9),
  });

  return (
    <SunshineCard
      alignItems="center"
      as={Link}
      isActive={isSelected}
      to={routes.getBankTransferRequestPath(request.teamMembersRequestId)}
    >
      <SunshineCard.Slot name="aside">
        <Avatar
          background="grey.100"
          borderColor="border"
          borderRadius="radius-10"
          borderWidth="thin"
          boxSize="space-32"
          color="text-primary"
          fontWeight="weight-500"
          name={recipientName}
        />
      </SunshineCard.Slot>
      <SimpleGrid
        alignItems="center"
        columns={3}
        spacing="space-24"
        templateColumns="6fr 3fr 3fr"
        width="100%"
      >
        <VStack alignItems="flex-start" spacing="space-0">
          <SunshineCard.Title>{recipientName}</SunshineCard.Title>
          <SunshineCard.Text>{formattedIban}</SunshineCard.Text>
        </VStack>
        <TeamMemberRequestStatusBadge
          from="managed-user"
          justifySelf="center"
          request={{
            status:
              request.status === TeamMembersRequestStatus.Rejected
                ? TeamMembersRequestStatus.Canceled
                : request.status,
            type: request.type,
          }}
        />
        <Text
          fontWeight={500}
          sx={{
            fontFeatureSettings: 'tnum',
            fontVariantNumeric: 'tabular-nums',
          }}
          textAlign="right"
          variant="primary"
        >
          {formatAmount({
            forcePlusSign: false,
            value: request.requesterPayload.amount / 100,
            withNoMinorUnitDigits: true,
          })}
        </Text>
      </SimpleGrid>
    </SunshineCard>
  );
};

export default BankTransferRequestListItem;
